/*
 * Copyright (C) 2023 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import { AfterViewInit, Component, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CoverImageUploadDialogComponent } from '@app/app/shared/components/cover-image-upload-dialog/cover-image-upload-dialog.component';
import { IMAGE_UPLOAD_DIALOG_CONFIG } from '@app/app/shared/models/config/dialog-config.model';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';
import { ImageUploadNameResolver } from '../../helpers/image-upload-name-resolver';
import { LanguageCodeHelper } from '../../helpers/language-code-helper';
import { SnackbarHelper } from '../../helpers/snackbar-helper';
import { Organization } from '../../models';
import { LinkToDestination, LinkToFeatureData, LinkToStyle } from '../../models/page/page.model';

@Component({
  templateUrl: './add-link-dialog.component.html',
  styleUrls: ['./add-link-dialog.component.scss'],
})
export class AddLinkDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private dialogRef: MatDialogRef<AddLinkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { editableLink: LinkToFeatureData },
    private translocoService: TranslocoService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private dialog: MatDialog,
  ) {}

  @Input() isContentStore: boolean;

  @Input() pageUid: string;

  @Output() linkToWeblinkSelected = new EventEmitter<LinkToFeatureData>();

  @Select(UserAuthState.organizationDetails)
  private organizationData$: Observable<Organization>;

  linkToData: LinkToFeatureData = this.resetLinkToData();
  organization: Organization;

  linkError: boolean;
  headlineError: boolean;
  textError: boolean;
  thumbnailUploadInProgress: boolean;

  private organizationSubscription: Subscription;

  ngOnInit() {
    this.organizationSubscription = this.organizationData$.subscribe((data) => {
      this.organization = data;
    });

    if (this.data?.editableLink) {
      this.linkToData = this.data.editableLink;
    }
  }

  ngAfterViewInit(): void {
    (document.querySelector('.f_close-btn') as HTMLElement)?.focus();
  }

  ngOnDestroy() {
    this.organizationSubscription?.unsubscribe();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  openCoverImageDialog(): void {
    const dialogRef = this.dialog.open(CoverImageUploadDialogComponent, {
      ...IMAGE_UPLOAD_DIALOG_CONFIG,
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      data: {
        advancedImageCrop: true,
        resizeToHeight: 300,
        cropperShape: 'rectangle',
      },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.croppedImageFile) {
        this.linkToData.file = data.croppedImageFile;
        this.linkToData.thumbnail = ImageUploadNameResolver.resolveImageName(data.croppedImageFile.name);
        this.linkToData.previewImage = data.croppedImageDataUrl;
      }
    });
  }

  onThumbnailRemove() {
    this.linkToData.file = null;
    this.linkToData.thumbnail = null;
    this.linkToData.previewImage = null;
  }

  cancel() {
    this.dialogRef.close();
  }

  addLinkToPlaylist() {
    if (!this.validateData()) {
      return;
    }

    this.dialogRef.close({
      playlistUid: undefined,
      cardUid: undefined,
      type: 'LINK',
      ...this.linkToData,
    });
  }

  resetLinkToData() {
    return {
      link: '',
      text: '',
      headline: '',
      subLine: '',
      fontColor: '#009FE3',
      backgroundColor: '#c9eef9',
      fontSize: 14,
      linkStyle: 'TEXT_COLOR_BACKGROUND' as LinkToStyle,
      destination: 'WEBLINK' as LinkToDestination,
      thumbnail: '',
      file: null,
      previewImage: '',
      hasAdminRights: true,
    };
  }

  private validateData() {
    this.linkError = false;
    this.textError = false;
    this.headlineError = false;

    if (this.linkToData.link === '') {
      this.linkError = true;
      return SnackbarHelper.showTranslatableSnackBar(
        this.ngZone,
        this.snackBar,
        this.translocoService,
        marker('translations.pages.message.error.emptyFormUrl'),
      );
    }
    if (this.linkToData.linkStyle === 'TEXT_COLOR_BACKGROUND' && this.linkToData.text === '') {
      this.textError = true;
      return SnackbarHelper.showTranslatableSnackBar(
        this.ngZone,
        this.snackBar,
        this.translocoService,
        marker('translations.pages.message.error.emptyFormText'),
      );
    }
    if (this.linkToData.linkStyle !== 'TEXT_COLOR_BACKGROUND' && this.linkToData.headline === '') {
      this.headlineError = true;
      return SnackbarHelper.showTranslatableSnackBar(
        this.ngZone,
        this.snackBar,
        this.translocoService,
        marker('translations.pages.message.error.emptyFormHeadline'),
      );
    }
    if (this.linkError || this.textError || this.headlineError) {
      document.querySelector('.link-to-section').scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      return false;
    }

    return true;
  }
}
